import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  UploadCloud,
  FileText,
  CheckCircle2,
  AlertCircle,
  ShieldCheck,
  Lock,
  User,
  Mail,
  Phone,
  Hash,
  X,
  Plus,
  Loader2,
  Sparkles,
  ArrowRight,
  ExternalLink,
  Info,
  Check,
  Building,
} from "lucide-react";
import axios from "axios";
import { useLang } from "../context/LanguageContext";

const DOCUMENT_CATEGORIES = [
  { id: "passport", fr: "Passeport international", en: "International Passport" },
  { id: "birth_certificate", fr: "Acte de naissance & Filiation", en: "Birth Certificate & Lineage" },
  { id: "marriage_certificate", fr: "Acte de mariage / Union", en: "Marriage Certificate / Partnership" },
  { id: "tcf_results", fr: "Attestation TCF / TEF Canada", en: "TCF / TEF Canada Test Report" },
  { id: "wes_report", fr: "Rapport d'évaluation WES / EDE", en: "WES / ECA Credential Assessment" },
  { id: "diplomas", fr: "Diplômes & Relevés de notes", en: "Diplomas & Transcripts" },
  { id: "work_experience", fr: "Attestations de travail, Contrats & CV", en: "Work Certificates & Resume" },
  { id: "police_clearance", fr: "Casier judiciaire / Certificat de police", en: "Police Clearance Certificate" },
  { id: "medical_exam", fr: "Visite médicale préalable (E-Medical)", en: "Upfront Medical Exam" },
  { id: "proof_of_funds", fr: "Preuve de fonds & Relevés bancaires", en: "Proof of Settlement Funds" },
  { id: "children_docs", fr: "Documents Enfants (Scolarité / Garde)", en: "Children Documents (School / Custody)" },
  { id: "other", fr: "Autre document justificatif", en: "Other Supporting Document" },
];

const QUICK_TITLE_SUGGESTIONS = {
  passport: ["Passeport - Page biométrique", "Passeport - Tampons d'entrée/sortie", "Passeport renouvelé 2026"],
  tcf_results: ["Attestation officielle TCF Canada", "Résultats TEF Canada - Niveaux B2/C1", "Attestation provisoire de test"],
  diplomas: ["Diplôme de Master 2 original", "Relevés de notes universitaires complets", "Licence professionnelle & attestation"],
  wes_report: ["Rapport WES d'équivalence canadienne", "Évaluation comparative des études"],
  work_experience: ["Attestation de travail employeur principal", "Contrat de travail & fiches de paie", "Curriculum Vitae actualisé format canadien"],
  proof_of_funds: ["Relevé bancaire officiel 6 derniers mois", "Attestation de solde bancaire certifiée", "Preuve d'origine des fonds"],
  birth_certificate: ["Acte de naissance légalisé", "Livret de famille complet"],
  police_clearance: ["Extrait de casier judiciaire Bulletin N°3", "Certificat de police international"],
};

export default function PublicDropBox() {
  const { lang, setLang } = useLang();
  const [searchParams] = useSearchParams();

  // Client Info State
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [fileReference, setFileReference] = useState("");

  // Staged Files State: array of { id, file, category, customTitle, familyMember, familyMemberName, notes }
  const [stagedFiles, setStagedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  // Upload Progress & Result
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [submittedDocs, setSubmittedDocs] = useState([]);
  const [confirmationCode, setConfirmationCode] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  // Pre-fill from query parameters if consultant shared tailored link
  useEffect(() => {
    const qName = searchParams.get("name") || searchParams.get("client") || "";
    const qEmail = searchParams.get("email") || "";
    const qPhone = searchParams.get("phone") || searchParams.get("whatsapp") || "";
    const qRef = searchParams.get("ref") || searchParams.get("dossier") || "";

    if (qName) setClientName(qName);
    if (qEmail) setClientEmail(qEmail);
    if (qPhone) setClientPhone(qPhone);
    if (qRef) setFileReference(qRef);
  }, [searchParams]);

  // Clean filename to provide an initial helpful meaningful name
  const suggestTitleFromFilename = (filename, categoryId) => {
    const clean = filename.replace(/\.[^/.]+$/, "").replace(/[_-]+/g, " ");
    return clean.charAt(0).toUpperCase() + clean.slice(1);
  };

  const handleFilesAdded = (filesList) => {
    const newItems = Array.from(filesList).map((f) => {
      const defaultCategory = "passport";
      return {
        id: `stage_${Math.random().toString(36).substring(2, 9)}`,
        file: f,
        category: defaultCategory,
        customTitle: suggestTitleFromFilename(f.name, defaultCategory),
        familyMember: "main_applicant",
        familyMemberName: "",
        notes: "",
      };
    });
    setStagedFiles((prev) => [...prev, ...newItems]);
    setErrorMessage("");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFilesAdded(e.dataTransfer.files);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFilesAdded(e.target.files);
      e.target.value = "";
    }
  };

  const updateStagedItem = (id, field, value) => {
    setStagedFiles((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  const removeStagedItem = (id) => {
    setStagedFiles((prev) => prev.filter((item) => item.id !== id));
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 Ko";
    const k = 1024;
    const sizes = ["Octets", "Ko", "Mo", "Go"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!clientName.trim()) {
      setErrorMessage(
        lang === "fr"
          ? "Veuillez renseigner votre nom complet afin que le consultant puisse identifier votre dossier."
          : "Please enter your full name so the consultant can identify your file."
      );
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    if (stagedFiles.length === 0) {
      setErrorMessage(
        lang === "fr"
          ? "Veuillez sélectionner au moins un document à déposer."
          : "Please select at least one document to upload."
      );
      return;
    }

    // Verify each file has a meaningful name
    const missingTitle = stagedFiles.find((item) => !item.customTitle.trim());
    if (missingTitle) {
      setErrorMessage(
        lang === "fr"
          ? `Veuillez attribuer un nom clair et significatif au document « ${missingTitle.file.name} ».`
          : `Please provide a meaningful name for document "${missingTitle.file.name}".`
      );
      return;
    }

    setIsSubmitting(true);
    setUploadProgress({ current: 0, total: stagedFiles.length });

    const uploadedList = [];
    const code = `JNIC-DROP-${Math.floor(10000 + Math.random() * 90000)}`;

    try {
      for (let i = 0; i < stagedFiles.length; i++) {
        const item = stagedFiles[i];
        setUploadProgress({ current: i + 1, total: stagedFiles.length });

        const formData = new FormData();
        formData.append("file", item.file);
        formData.append("client_name", clientName.trim());
        if (clientEmail.trim()) formData.append("client_email", clientEmail.trim());
        if (clientPhone.trim()) formData.append("client_phone", clientPhone.trim());
        if (fileReference.trim()) formData.append("file_reference", fileReference.trim());
        formData.append("document_type", item.category);
        formData.append("custom_title", item.customTitle.trim());
        formData.append("family_member", item.familyMember || "main_applicant");
        if (item.familyMemberName.trim()) {
          formData.append("family_member_name", item.familyMemberName.trim());
        }
        if (item.notes.trim()) {
          formData.append("client_notes", item.notes.trim());
        }

        const res = await axios.post("/api/documents/drop-box/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (res.data?.ok && res.data?.document) {
          uploadedList.push(res.data.document);
        }
      }

      setSubmittedDocs(uploadedList);
      setConfirmationCode(code);
      setStagedFiles([]);
    } catch (err) {
      console.error("DropBox upload error:", err);
      setErrorMessage(
        err.response?.data?.detail ||
          (lang === "fr"
            ? "Une erreur est survenue lors de l'envoi de vos documents. Veuillez vérifier votre connexion et réessayer."
            : "An error occurred while uploading your documents. Please verify your connection and try again.")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetForMore = () => {
    setConfirmationCode(null);
    setSubmittedDocs([]);
    setStagedFiles([]);
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] pt-12 pb-24 text-[#06162A]">
      {/* Top Header & Breadcrumb */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between py-3 border-b border-[#06162A]/10 mb-8">
          <Link to="/" className="flex items-center gap-2 group">
            <img
              src="/logo.png"
              alt="JNIC"
              className="w-9 h-9 rounded-full object-cover ring-1 ring-[#D7A33D]/40 group-hover:scale-105 transition"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = "/jnic-premium-logo.png";
              }}
            />
            <span className="font-serif font-bold text-sm tracking-wide text-[#06162A]">
              JN Immigration Consulting
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 text-xs font-medium border border-emerald-200">
              <Lock size={12} className="text-emerald-600" />
              <span>{lang === "fr" ? "Dépôt sécurisé SSL" : "SSL Encrypted"}</span>
            </span>
            <button
              onClick={() => setLang(lang === "fr" ? "en" : "fr")}
              className="px-2.5 py-1 rounded-lg border border-[#06162A]/15 text-xs font-semibold hover:bg-white transition"
            >
              {lang === "fr" ? "EN" : "FR"}
            </button>
          </div>
        </div>

        {/* Hero Title */}
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D7A33D]/15 text-[#976a16] text-xs font-bold uppercase tracking-widest mb-3">
            <UploadCloud size={14} />
            <span>{lang === "fr" ? "Boîte de Dépôt Client Sans Connexion" : "Guest Document Drop Box"}</span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#06162A] font-bold tracking-tight">
            {lang === "fr" ? "Transmettez vos documents en toute sécurité" : "Upload your documents securely"}
          </h1>
          <p className="mt-3 text-sm sm:text-base text-[#06162A]/70 leading-relaxed">
            {lang === "fr"
              ? "Aucun compte ni mot de passe requis. Vos fichiers sont directement chiffrés et transmis à notre équipe de consultants réglementés CRIC pour l'évaluation de votre dossier d'immigration au Canada."
              : "No account or password required. Your documents are securely encrypted and delivered directly to our regulated Canadian immigration consultants."}
          </p>
        </div>

        {/* Success Confirmation Screen */}
        {confirmationCode ? (
          <div className="bg-white rounded-3xl p-8 sm:p-10 border border-[#06162A]/10 shadow-lg text-center animate-fade-in">
            <div className="w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 size={36} />
            </div>
            <span className="text-xs uppercase tracking-widest text-[#D71920] font-bold">
              {lang === "fr" ? "Transmission Réussie" : "Transmission Successful"}
            </span>
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#06162A] mt-1 mb-2">
              {lang === "fr" ? "Documents reçus avec succès !" : "Documents successfully received!"}
            </h2>
            <p className="text-sm text-[#06162A]/70 max-w-lg mx-auto">
              {lang === "fr"
                ? `Merci ${clientName}, vos pièces justificatives ont bien été transmises au cabinet JNIC. Notre équipe les examinera sous peu.`
                : `Thank you ${clientName}, your supporting documents have been delivered to JNIC. Our team will review them promptly.`}
            </p>

            <div className="mt-6 inline-flex items-center gap-2 bg-[#FAF8F5] px-4 py-2.5 rounded-2xl border border-[#06162A]/10 text-xs font-mono font-bold text-[#06162A]">
              <Hash size={14} className="text-[#D7A33D]" />
              <span>{lang === "fr" ? "Numéro d'accusé :" : "Receipt Ref:"}</span>
              <span className="text-[#D71920]">{confirmationCode}</span>
            </div>

            {/* Recap of uploaded documents */}
            <div className="mt-8 text-left border-t border-[#06162A]/10 pt-6">
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#06162A]/60 mb-3">
                {lang === "fr" ? "Récapitulatif des pièces transmises :" : "Summary of uploaded documents:"}
              </h3>
              <div className="space-y-2">
                {submittedDocs.map((doc, idx) => (
                  <div
                    key={idx}
                    className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80 flex items-center justify-between text-xs"
                  >
                    <div className="flex items-center gap-3">
                      <FileText size={18} className="text-[#D7A33D] shrink-0" />
                      <div>
                        <p className="font-semibold text-[#06162A] text-sm">{doc.custom_title}</p>
                        <p className="text-[#06162A]/50 text-[11px]">
                          {lang === "fr" ? doc.document_category_label_fr : doc.document_category_label_en} ·{" "}
                          {doc.original_filename}
                        </p>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200">
                      <Check size={12} />
                      {lang === "fr" ? "Enregistré" : "Uploaded"}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-[#06162A]/10 flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                type="button"
                onClick={handleResetForMore}
                className="w-full sm:w-auto px-6 py-3 rounded-full bg-[#06162A] text-white text-xs font-bold hover:bg-[#D71920] transition"
              >
                {lang === "fr" ? "Déposer d'autres documents" : "Upload more documents"}
              </button>
              <Link
                to="/"
                className="w-full sm:w-auto px-6 py-3 rounded-full border border-[#06162A]/20 text-[#06162A] text-xs font-bold hover:bg-white transition"
              >
                {lang === "fr" ? "Retour à l'accueil JNIC" : "Return to JNIC Home"}
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Error Banner */}
            {errorMessage && (
              <div className="p-4 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-xs flex items-start gap-2.5">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <p>{errorMessage}</p>
              </div>
            )}

            {/* STEP 1: Client Information */}
            <div className="bg-white rounded-3xl p-6 sm:p-8 border border-[#06162A]/10 shadow-xs">
              <div className="flex items-center gap-2.5 mb-5 pb-3 border-b border-[#06162A]/10">
                <div className="w-8 h-8 rounded-full bg-[#06162A] text-white flex items-center justify-center text-xs font-bold">
                  1
                </div>
                <div>
                  <h2 className="font-serif text-lg font-bold text-[#06162A]">
                    {lang === "fr" ? "Vos Coordonnées (Pour identifier votre dossier)" : "Your Details (File Identification)"}
                  </h2>
                  <p className="text-xs text-[#06162A]/50">
                    {lang === "fr"
                      ? "Indiquez votre nom complet tel qu'il figure sur votre passeport."
                      : "Enter your full name as shown on your international passport."}
                  </p>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-bold text-[#06162A] mb-1.5">
                    {lang === "fr" ? "Nom complet *" : "Full Name *"}
                  </label>
                  <div className="relative">
                    <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#06162A]/40" />
                    <input
                      type="text"
                      required
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      placeholder={lang === "fr" ? "Ex. Jean-Luc Mbarga ou Josiane Tremblay" : "e.g. John Doe"}
                      className="w-full h-11 pl-10 pr-4 rounded-xl border border-[#06162A]/15 bg-white text-xs text-[#06162A] focus:outline-none focus:border-[#D71920] focus:ring-1 focus:ring-[#D71920]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#06162A] mb-1.5">
                    {lang === "fr" ? "Email (Pour accusé de réception)" : "Email (For receipt confirmation)"}
                  </label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#06162A]/40" />
                    <input
                      type="email"
                      value={clientEmail}
                      onChange={(e) => setClientEmail(e.target.value)}
                      placeholder="nom@exemple.com"
                      className="w-full h-11 pl-10 pr-4 rounded-xl border border-[#06162A]/15 bg-white text-xs text-[#06162A] focus:outline-none focus:border-[#D71920] focus:ring-1 focus:ring-[#D71920]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#06162A] mb-1.5">
                    {lang === "fr" ? "Téléphone / WhatsApp" : "Phone / WhatsApp"}
                  </label>
                  <div className="relative">
                    <Phone size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#06162A]/40" />
                    <input
                      type="tel"
                      value={clientPhone}
                      onChange={(e) => setClientPhone(e.target.value)}
                      placeholder="+237 ... ou +1 ..."
                      className="w-full h-11 pl-10 pr-4 rounded-xl border border-[#06162A]/15 bg-white text-xs text-[#06162A] focus:outline-none focus:border-[#D71920] focus:ring-1 focus:ring-[#D71920]"
                    />
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-xs font-bold text-[#06162A] mb-1.5">
                    {lang === "fr" ? "Référence ou programme concerné (Optionnel)" : "File reference or program (Optional)"}
                  </label>
                  <div className="relative">
                    <Hash size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#06162A]/40" />
                    <input
                      type="text"
                      value={fileReference}
                      onChange={(e) => setFileReference(e.target.value)}
                      placeholder={
                        lang === "fr"
                          ? "Ex. Dossier Entrée Express, Permis d'études Université Laval, Parrainage..."
                          : "e.g. Express Entry file, Study Permit, Spousal Sponsorship..."
                      }
                      className="w-full h-11 pl-10 pr-4 rounded-xl border border-[#06162A]/15 bg-white text-xs text-[#06162A] focus:outline-none focus:border-[#D71920] focus:ring-1 focus:ring-[#D71920]"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* STEP 2: Document Drag & Drop + Staged Items */}
            <div className="bg-white rounded-3xl p-6 sm:p-8 border border-[#06162A]/10 shadow-xs">
              <div className="flex items-center justify-between mb-5 pb-3 border-b border-[#06162A]/10">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-[#06162A] text-white flex items-center justify-center text-xs font-bold">
                    2
                  </div>
                  <div>
                    <h2 className="font-serif text-lg font-bold text-[#06162A]">
                      {lang === "fr" ? "Sélection & Nommage Significatif des Documents" : "Upload & Meaningful Naming"}
                    </h2>
                    <p className="text-xs text-[#06162A]/50">
                      {lang === "fr"
                        ? "Chaque fichier doit avoir une catégorie et un intitulé clair pour accélérer l'analyse de votre dossier."
                        : "Each file must have a category and clear title to ensure swift evaluation."}
                    </p>
                  </div>
                </div>
                {stagedFiles.length > 0 && (
                  <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#D7A33D]/20 text-[#976a16]">
                    {stagedFiles.length} {lang === "fr" ? "prêt(s)" : "ready"}
                  </span>
                )}
              </div>

              {/* Drag & Drop Zone */}
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all ${
                  isDragging
                    ? "border-[#D71920] bg-red-50/40"
                    : "border-[#06162A]/20 hover:border-[#D7A33D] bg-[#FAF8F5]/60"
                }`}
              >
                <div className="w-14 h-14 rounded-2xl bg-white text-[#D7A33D] shadow-2xs border border-[#06162A]/10 flex items-center justify-center mx-auto mb-3">
                  <UploadCloud size={28} />
                </div>
                <p className="font-serif text-base font-bold text-[#06162A]">
                  {lang === "fr"
                    ? "Glissez vos fichiers ici ou cliquez pour choisir"
                    : "Drag your files here or click to browse"}
                </p>
                <p className="text-xs text-[#06162A]/50 mt-1 max-w-md mx-auto">
                  {lang === "fr"
                    ? "PDF, JPG, PNG, DOCX jusqu'à 25 Mo par fichier. Vous pourrez ensuite attribuer un nom clair à chaque pièce."
                    : "PDF, JPG, PNG, DOCX up to 25 MB per file. You will name each document before sending."}
                </p>

                <label className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#06162A] text-white text-xs font-bold hover:bg-[#D71920] transition cursor-pointer shadow-xs">
                  <Plus size={14} />
                  <span>{lang === "fr" ? "Choisir des fichiers sur mon appareil" : "Browse files from device"}</span>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.zip"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </label>
              </div>

              {/* Important Instruction Notice */}
              <div className="mt-6 p-4 rounded-2xl bg-amber-50/80 border border-amber-200/80 text-amber-900 text-xs flex items-start gap-3">
                <Info size={16} className="text-amber-700 shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold">
                    {lang === "fr"
                      ? "Conseil IRCC : Donnez un nom significatif à chaque pièce"
                      : "IRCC Recommendation: Give a meaningful name to each file"}
                  </p>
                  <p className="mt-0.5 text-amber-800/85 leading-relaxed">
                    {lang === "fr"
                      ? "Évitez les noms génériques tels que « Scan_001.pdf » ou « Photo.jpg ». Spécifiez précisément le contenu (ex. « Passeport biométrique 2026 » ou « Relevé bancaire 6 mois »). Cela évite tout refus ou retard de traitement."
                      : "Avoid generic names like 'Scan_001.pdf'. Clearly specify the content (e.g. 'Biometric Passport 2026' or '6 Months Bank Statements')."}
                  </p>
                </div>
              </div>

              {/* Staged Items List */}
              {stagedFiles.length > 0 && (
                <div className="mt-8 space-y-5">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-[#06162A]/70 flex items-center justify-between">
                    <span>{lang === "fr" ? "Documents prêts à être transmis :" : "Documents to upload:"}</span>
                    <label className="text-[11px] font-semibold text-[#D71920] hover:underline cursor-pointer">
                      + {lang === "fr" ? "Ajouter un autre fichier" : "Add another file"}
                      <input
                        type="file"
                        multiple
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.zip"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                    </label>
                  </h3>

                  {stagedFiles.map((item, idx) => (
                    <div
                      key={item.id}
                      className="p-5 rounded-2xl border border-[#06162A]/12 bg-[#FAF8F5] shadow-xs relative transition"
                    >
                      {/* Top Header of the Card */}
                      <div className="flex items-start justify-between gap-3 pb-3 mb-4 border-b border-[#06162A]/8">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-white border border-[#06162A]/10 text-[#D7A33D] flex items-center justify-center shrink-0">
                            <FileText size={20} />
                          </div>
                          <div>
                            <p className="text-xs font-bold text-[#06162A] truncate max-w-xs sm:max-w-md">
                              {item.file.name}
                            </p>
                            <p className="text-[11px] text-[#06162A]/50">
                              {formatFileSize(item.file.size)} · Document #{idx + 1}
                            </p>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => removeStagedItem(item.id)}
                          className="p-1.5 rounded-lg text-[#06162A]/40 hover:text-red-600 hover:bg-red-50 transition"
                          title={lang === "fr" ? "Supprimer ce fichier" : "Remove file"}
                        >
                          <X size={16} />
                        </button>
                      </div>

                      {/* Naming and Category Fields */}
                      <div className="grid sm:grid-cols-2 gap-4">
                        {/* Document Category Dropdown */}
                        <div>
                          <label className="block text-[11px] font-bold uppercase tracking-wider text-[#06162A]/70 mb-1">
                            {lang === "fr" ? "Catégorie de document *" : "Document Category *"}
                          </label>
                          <select
                            value={item.category}
                            onChange={(e) => updateStagedItem(item.id, "category", e.target.value)}
                            className="w-full h-10 px-3 rounded-xl border border-[#06162A]/15 bg-white text-xs text-[#06162A] font-medium focus:outline-none focus:border-[#D71920]"
                          >
                            {DOCUMENT_CATEGORIES.map((cat) => (
                              <option key={cat.id} value={cat.id}>
                                {lang === "fr" ? cat.fr : cat.en}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Meaningful Name Input */}
                        <div>
                          <label className="block text-[11px] font-bold uppercase tracking-wider text-[#06162A]/70 mb-1 flex items-center justify-between">
                            <span>{lang === "fr" ? "Nom significatif / Intitulé clair *" : "Meaningful Name *"}</span>
                            <span className="text-[10px] text-emerald-700 font-semibold flex items-center gap-1">
                              <Sparkles size={11} />
                              {lang === "fr" ? "Clé pour IRCC" : "Key for IRCC"}
                            </span>
                          </label>
                          <input
                            type="text"
                            required
                            value={item.customTitle}
                            onChange={(e) => updateStagedItem(item.id, "customTitle", e.target.value)}
                            placeholder={
                              lang === "fr"
                                ? "Ex. Passeport biométrique valide 2029"
                                : "e.g. Valid Biometric Passport 2029"
                            }
                            className="w-full h-10 px-3 rounded-xl border border-[#06162A]/15 bg-white text-xs text-[#06162A] font-medium focus:outline-none focus:border-[#D71920]"
                          />
                        </div>

                        {/* Quick Suggestions Chips */}
                        {QUICK_TITLE_SUGGESTIONS[item.category] && (
                          <div className="sm:col-span-2">
                            <p className="text-[10px] font-semibold text-[#06162A]/50 mb-1.5">
                              {lang === "fr" ? "💡 Suggestions rapides en 1 clic :" : "💡 1-Click title suggestions:"}
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {QUICK_TITLE_SUGGESTIONS[item.category].map((sugg, sIdx) => (
                                <button
                                  key={sIdx}
                                  type="button"
                                  onClick={() => updateStagedItem(item.id, "customTitle", sugg)}
                                  className="text-[10px] px-2.5 py-1 rounded-full bg-white hover:bg-[#D7A33D]/15 border border-[#06162A]/10 text-[#06162A]/80 transition text-left"
                                >
                                  + {sugg}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Family member target */}
                        <div>
                          <label className="block text-[11px] font-bold uppercase tracking-wider text-[#06162A]/70 mb-1">
                            {lang === "fr" ? "Personne concernée" : "Applicant / Family member"}
                          </label>
                          <select
                            value={item.familyMember}
                            onChange={(e) => updateStagedItem(item.id, "familyMember", e.target.value)}
                            className="w-full h-10 px-3 rounded-xl border border-[#06162A]/15 bg-white text-xs text-[#06162A] focus:outline-none focus:border-[#D71920]"
                          >
                            <option value="main_applicant">
                              {lang === "fr" ? "Demandeur Principal" : "Principal Applicant"}
                            </option>
                            <option value="spouse">{lang === "fr" ? "Conjoint(e) / Époux(se)" : "Spouse / Partner"}</option>
                            <option value="child">{lang === "fr" ? "Enfant à charge" : "Dependent Child"}</option>
                            <option value="other">{lang === "fr" ? "Autre membre" : "Other Member"}</option>
                          </select>
                        </div>

                        {/* Clarification Notes */}
                        <div>
                          <label className="block text-[11px] font-bold uppercase tracking-wider text-[#06162A]/70 mb-1">
                            {lang === "fr" ? "Remarque pour le consultant (Optionnel)" : "Note for consultant (Optional)"}
                          </label>
                          <input
                            type="text"
                            value={item.notes}
                            onChange={(e) => updateStagedItem(item.id, "notes", e.target.value)}
                            placeholder={
                              lang === "fr"
                                ? "Ex. Traduction assermentée jointe en page 2..."
                                : "e.g. Certified translation attached on page 2..."
                            }
                            className="w-full h-10 px-3 rounded-xl border border-[#06162A]/15 bg-white text-xs text-[#06162A] focus:outline-none focus:border-[#D71920]"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Submit Action Bar */}
            <div className="bg-white rounded-3xl p-6 sm:p-8 border border-[#06162A]/10 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
                  <ShieldCheck size={22} />
                </div>
                <div>
                  <p className="text-xs font-bold text-[#06162A]">
                    {lang === "fr" ? "Confidentialité & Chiffrement Garantis" : "Guaranteed Privacy & Encryption"}
                  </p>
                  <p className="text-[11px] text-[#06162A]/50">
                    {lang === "fr"
                      ? "Conforme aux normes de protection des renseignements personnels (Loi 25 & LPRPDE)."
                      : "Compliant with Canadian personal information privacy acts (PIPEDA)."}
                  </p>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || stagedFiles.length === 0}
                className="w-full sm:w-auto h-12 px-8 rounded-full bg-[#06162A] hover:bg-[#D71920] text-white text-xs font-bold transition flex items-center justify-center gap-2 shadow-md disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>
                      {lang === "fr"
                        ? `Transmission du document ${uploadProgress.current}/${uploadProgress.total}...`
                        : `Uploading document ${uploadProgress.current}/${uploadProgress.total}...`}
                    </span>
                  </>
                ) : (
                  <>
                    <UploadCloud size={16} />
                    <span>
                      {lang === "fr"
                        ? `Transmettre ${stagedFiles.length} document${stagedFiles.length > 1 ? "s" : ""} à JNIC`
                        : `Submit ${stagedFiles.length} document${stagedFiles.length > 1 ? "s" : ""} to JNIC`}
                    </span>
                    <ArrowRight size={14} />
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
